import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/ar/app/trainer/id1663869646';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.mas10.trainer';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DownloadModal: React.FC<DownloadModalProps> = ({ isOpen, onClose }) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-mas10-primary rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Content */}
            <div className="relative p-8 pt-12 pb-10">
              <h2 className="text-center text-white text-xl md:text-2xl font-bold uppercase tracking-wide mb-4 mt-6">
                ¡Empezá como entrenador en +Trainer!
              </h2>
              <p className="text-center text-white/90 text-sm md:text-base mb-6">
                Descargá la app, creá tu cuenta como entrenador y probá la plataforma con un período de prueba.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white hover:bg-gray-50 text-gray-900 rounded-xl px-5 py-3.5 w-full sm:w-auto min-w-[200px] transition-colors shadow-lg"
                  aria-label="Descargar en App Store"
                >
                  <svg className="w-10 h-10 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <span className="block text-[10px] leading-tight">Download on the</span>
                    <span className="block text-base font-semibold leading-tight">App Store</span>
                  </div>
                </a>

                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white hover:bg-gray-50 text-gray-900 rounded-xl px-5 py-3.5 w-full sm:w-auto min-w-[200px] transition-colors shadow-lg"
                  aria-label="Disponible en Google Play"
                >
                  <svg className="w-9 h-9 flex-shrink-0 text-gray-900" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7L8 5z"/>
                  </svg>
                  <div className="text-left">
                    <span className="block text-[10px] leading-tight">Available on the</span>
                    <span className="block text-base font-semibold leading-tight">Google Play</span>
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DownloadModal;
